import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import "./Quality.css";
import { qualityApi } from "./qualityApi";
import { Bars, Kpi, MockBanner, People, RingKpi, fmtShort, hasPerm, pct } from "./qualityUi";
import RaiseBugModal from "./RaiseBugModal";

/**
 * Quality — the management view.
 *
 * Answers "are we getting better": how many bugs reach production, where they
 * cluster, what root causes keep coming back, and which corrective/preventive
 * actions are still open.
 */
export default function QualityDashboard() {
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const [raising, setRaising] = useState(false);

    useEffect(() => {
        qualityApi.getDashboard()
            .then((res) => setD(res.data))
            .catch((e) => setErr(e.message || "Could not load the dashboard."));
    }, []);

    if (err) return <div className="qa-page"><MockBanner /><div className="qa-empty">{err}</div></div>;
    if (!d) return <div className="qa-page"><MockBanner /><div className="qa-empty">Loading…</div></div>;

    const t = d.totals;
    const latest = d.latest_release;
    const current = d.current_release;
    const maxLeak = Math.max(1, ...d.leakage_by_release.map((r) => r.leakage));
    const today = new Date().toISOString().slice(0, 10);
    const topModule = d.by_module[0];

    return (
        <div className="qa-page">
            <MockBanner />
            <div className="qa-head">
                <h1>Quality Dashboard</h1>
                <span className="qa-hint">How production bugs, releases and regression testing are trending</span>
                <span className="qa-sp" />
                {hasPerm("Log Production Bug") && (
                    <button className="qa-btn qa-btn--primary" onClick={() => setRaising(true)}>
                        <FiPlus size={12} /> Log production bug
                    </button>
                )}
            </div>

            <div className="qa-kpis">
                <RingKpi percent={t.bugs ? (t.closed / t.bugs) * 100 : 0} value={t.bugs} label="Production bugs (closed %)" />
                <Kpi value={t.open} label="Open" color="var(--qa-orange)" fill={t.bugs ? (t.open / t.bugs) * 100 : 0} />
                <Kpi value={t.critical_open} label="P0 / P1 open" color="var(--qa-red)" fill={t.open ? (t.critical_open / t.open) * 100 : 0} />
                {latest && <Kpi value={latest.escaped} label={`Escaped from ${latest.version}`} color="var(--qa-blue)" fill={latest.escaped * 20} />}
                {latest && <Kpi value={pct(latest.leakage)} label={`Defect leakage, ${latest.version}`} color="var(--qa-purple)" fill={latest.leakage} />}
                {current && (
                    <Kpi
                        value={`${current.coverage}%`} label={`Regression run, ${current.version}`}
                        color={current.coverage === 100 ? "var(--qa-green)" : "var(--qa-amber)"} fill={current.coverage}
                    />
                )}
            </div>

            <div className="qa-dgrid">
                <div className="qa-box">
                    <div className="qa-sec">
                        <h4>Production bugs by module</h4>
                        <Bars rows={d.by_module} />
                        {topModule && (
                            <p className="qa-hint" style={{ margin: "10px 0 0" }}>
                                {topModule.label} has the most production bugs. Review its regression cases first.
                            </p>
                        )}
                    </div>
                </div>
                <div className="qa-box">
                    <div className="qa-sec">
                        <h4>Defect leakage by release</h4>
                        <div className="qa-vbars">
                            {d.leakage_by_release.map((r) => (
                                <div className="qa-vbars__c" key={r.version}>
                                    <span className="qa-num">{pct(r.leakage)}</span>
                                    <i style={{ height: Math.max(4, (r.leakage / maxLeak) * 110) }} />
                                </div>
                            ))}
                        </div>
                        <div className="qa-vlab">
                            {d.leakage_by_release.map((r) => <span key={r.version} className="qa-mono">{r.version}</span>)}
                        </div>
                        <p className="qa-hint" style={{ margin: "10px 0 0" }}>
                            Production bugs ÷ (production bugs + bugs QA found before release).
                        </p>
                    </div>
                </div>
                <div className="qa-box">
                    <div className="qa-sec">
                        <h4>Where open bugs are</h4>
                        <Bars rows={d.by_stage} />
                    </div>
                </div>
                <div className="qa-box">
                    <div className="qa-sec">
                        <h4>Root causes found</h4>
                        {d.root_causes.length
                            ? <Bars rows={d.root_causes} color="var(--qa-orange)" labelWidth={150} />
                            : <p className="qa-hint">No analyses yet.</p>}
                    </div>
                </div>
            </div>

            <div className="qa-toolbar" style={{ marginTop: 16 }}>
                <span className="qa-toolbar__count">Open corrective &amp; preventive actions · {d.open_actions.length}</span>
            </div>
            <div className="qa-card">
                <table className="qa-tbl">
                    <thead>
                        <tr><th>Bug</th><th>Type</th><th>Action</th><th>Owner</th><th>Due</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {d.open_actions.length === 0 && (
                            <tr><td colSpan={6} className="qa-empty">No open actions.</td></tr>
                        )}
                        {d.open_actions.map((a) => {
                            const overdue = a.due_date && a.due_date < today;
                            return (
                                <tr key={a.id} className="qa-click" onClick={() => navigate(`/quality/bugs/${a.bug_id}`)}>
                                    <td className="qa-code">{a.bug_code}</td>
                                    <td>
                                        <span className={`qa-chip ${a.type === "Corrective" ? "qa-chip--blue" : "qa-chip--green"}`}>{a.type}</span>
                                    </td>
                                    <td>{a.description}</td>
                                    <td><People names={[a.owner]} /></td>
                                    <td className="qa-nw" style={{ color: overdue ? "var(--qa-red)" : undefined }}>
                                        {fmtShort(a.due_date)}{overdue ? " · overdue" : ""}
                                    </td>
                                    <td>{a.status}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {raising && (
                <RaiseBugModal
                    onClose={() => setRaising(false)}
                    onCreated={(b) => navigate(`/quality/bugs/${b.id}`, { state: { tab: "trace" } })}
                />
            )}
        </div>
    );
}
